import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  CheckCircle,
  Download,
  Plus,
  Key,
  Trash2,
  X,
  Eye,
  EyeOff,
  Copy,
  Check,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { LocationApiConfigModal } from "../LocationApiConfigModal";
import {
  MarketplaceApp,
  useMarketAppSettingsData,
  Location,
} from "@/lib/settings/marketplaceData";
import {
  createCompanyMarketApp,
  deleteCompanyMarketApp,
  createAccount,
  recreateLink,
  validateAccountRegistered,
  getCompanies,
  getCompanyMarketplaceApps,
  getCompanyMarketplaceAppsByCompanyId,
} from "@/dispatchers/marketAppDispacher";
import MarketplaceSettingsSkeleton from "./skeletons/MarketplaceSettingsSkeleton";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import {
  showError,
  showSuccess,
} from "@/components/ToastedMessage/ToastedMessage";
import { Skeleton } from "@mui/material"; // Add this import
import useTitanApp from "@/hooks/useTitanApp";

interface InstalledAppData {
  locations: string[];
  apiKeys: Record<string, any>;
}

interface MarketplaceSettingsTabProps {
  isDarkMode?: boolean;
  onViewApp: (app: MarketplaceApp) => void;
}

interface AppDetailsPanelProps {
  app: MarketplaceApp;
  installedAppData?: InstalledAppData;
  locations: Location[];
  isDarkMode?: boolean;
  onInstall: (
    appId: string,
    selectedLocationIds: string[],
    apiKeys: Record<string, any>,
  ) => void;
  onUninstall: (appId: string, locationId?: string) => void;
  onApiKeysUpdate: (
    appId: string,
    locationId: string,
    apiKeys: Record<string, any>,
  ) => void;
  onBack: () => void;
}

export function MarketplaceSettingsTab({
  onViewApp,
}: MarketplaceSettingsTabProps) {
  const {
    isDarkMode,
    marketplaceApps,
    installedApps,
    users,
    isMarketAppsLoading,
    MarketAppsBaseLoader
  } = useMarketAppSettingsData();

  const { appState } = useTitanApp();
  const { companyDetails, features } = appState || {};
  const { companySubscriptionStatus } = companyDetails || {};
  const { ifAlliedHealthTrainingEnabled } = features || {};

  const filteredApps = marketplaceApps.filter((app) => {
    if (app.name?.trim().toLowerCase() === "consultation bundles") {
      return companySubscriptionStatus === 2;
    }
    if (app.marketType === 5) {
      return ifAlliedHealthTrainingEnabled;
    }
    return true;
  });

  if (MarketAppsBaseLoader) {
    return <MarketplaceSettingsSkeleton />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Market Place
          </h2>
          <p
            className={`text-sm mt-1 transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Extend TITANVERSE with powerful integrations and add-ons
          </p>
        </div>
        <Badge variant="secondary" className="text-xs">
          {installedApps.size} app{installedApps.size !== 1 ? "s" : ""}{" "}
          installed
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {filteredApps.map((app) => {
          const installedAppData = installedApps.get(app.id);
          const isInstalled = !!installedAppData;
          const installedLocationCount =
            installedAppData?.locations?.length || 0;

          return (
            <div
              key={app.id}
              className={`border rounded-lg p-6 hover:shadow-md transition-all duration-300 cursor-pointer ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800 hover:bg-gray-750"
                  : "border-gray-200 hover:shadow-md"
              }`}
              onClick={() => onViewApp(app)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">
                    {app.imageUrl ? (
                      <img
                        src={app.imageUrl}
                        alt={app.name}
                        className="h-8 w-auto max-w-[110px] object-contain"
                      />
                    ) : (
                      app.imageEmoji
                    )}
                  </div>
                  <div>
                    <h3
                      className={`font-semibold flex items-center gap-2 transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {app.name}
                      {isInstalled && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </h3>
                    <p
                      className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      by {app.developer}
                    </p>
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {app.category}
                </Badge>
              </div>

              <p
                className={`text-sm mb-4 line-clamp-2 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {app.description}
              </p>

              {isInstalled && (
                <div
                  className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? "text-green-400" : "text-green-600"
                  }`}
                >
                  {app.name?.trim().toLowerCase() === "ryft"
                    ? "Installed (Global)"
                    : app.name?.trim().toLowerCase() === "demdx"
                    ? `${
                        Object.keys(installedAppData.apiKeys || {}).length
                      } license${
                        Object.keys(installedAppData.apiKeys || {}).length !== 1
                          ? "s"
                          : ""
                      } assigned`
                    : app.name?.trim().toLowerCase() === "consultation bundles"
                    ? `${(installedAppData.apiKeys?.purchases || [])
                        .reduce((s: number, p: any) => s + (p.consults || 0), 0)
                        .toLocaleString()} consult credits purchased`
                    : installedLocationCount > 0
                    ? `Installed on ${installedLocationCount} location${
                        installedLocationCount !== 1 ? "s" : ""
                      }`
                    : "Installed"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppDetailsSkeleton({ isDarkMode }: { isDarkMode?: boolean }) {
  const sx = { bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined };
  const cardClass = `border rounded-lg p-4 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
  }`;

  return (
    <div className="p-6 space-y-6">
      <Skeleton variant="rounded" width={80} height={32} sx={sx} />

      <div className="flex items-center gap-4">
        <Skeleton variant="rounded" width={64} height={64} sx={sx} />
        <div>
          <Skeleton variant="text" width={200} height={36} sx={sx} />
          <Skeleton variant="text" width={120} height={20} sx={sx} />
          <Skeleton
            variant="rounded"
            width={80}
            height={22}
            sx={{ ...sx, mt: 1 }}
          />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div>
            <Skeleton variant="text" width={80} height={28} sx={sx} />
            <Skeleton variant="text" width="100%" height={20} sx={sx} />
            <Skeleton variant="text" width="90%" height={20} sx={sx} />
            <Skeleton variant="text" width="60%" height={20} sx={sx} />
          </div>

          <div>
            <Skeleton variant="text" width={100} height={28} sx={sx} />
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center gap-2 mt-2">
                <Skeleton variant="circular" width={16} height={16} sx={sx} />
                <Skeleton variant="text" width={180} height={20} sx={sx} />
              </div>
            ))}
          </div>

          <div>
            <Skeleton variant="text" width={160} height={28} sx={sx} />
            <div className={`${cardClass} mt-3 space-y-3`}>
              <div className="flex items-center justify-between">
                <div>
                  <Skeleton variant="text" width={140} height={22} sx={sx} />
                  <Skeleton variant="text" width={200} height={16} sx={sx} />
                </div>
                <div className="flex gap-2">
                  <Skeleton variant="rounded" width={90} height={30} sx={sx} />
                  <Skeleton variant="rounded" width={30} height={30} sx={sx} />
                </div>
              </div>
              <Skeleton variant="text" width={120} height={16} sx={sx} />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className={cardClass}>
            <Skeleton variant="text" width={70} height={24} sx={sx} />
            <Skeleton variant="text" width="60%" height={20} sx={sx} />
            <Skeleton variant="text" width="80%" height={20} sx={sx} />
          </div>
          <Skeleton variant="rounded" width="100%" height={40} sx={sx} />
          <Skeleton variant="rounded" width="100%" height={40} sx={sx} />
        </div>
      </div>
    </div>
  );
}

export function AppDetailsPanel({
  app,
  //installedAppData,
  onInstall,
  onUninstall,
  onApiKeysUpdate,
  onBack,
}: AppDetailsPanelProps) {
  const {
    isDarkMode,
    locations,
    users,
    companyId,
    refreshInstalledApps,
    installedApps,
    currentUser,
    isMarketAppsLoading,
    IsMarketplaceAppLoading,
  } = useMarketAppSettingsData();
  const { appState } = useTitanApp();
  const { currentPharmacyId } = appState || {};
  const installedAppData = installedApps.get(app.id);
  const [secretKey, setSecretKey] = useState<string>("");
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const isInstalled = !!installedAppData;
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [locationApiKeys, setLocationApiKeys] = useState<Record<string, any>>(
    {},
  );
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userApiKeys, setUserApiKeys] = useState<Record<string, any>>({});

  const [showInstallModal, setShowInstallModal] = useState(false);
  const [activeLocationConfig, setActiveLocationConfig] = useState<
    string | null
  >(null);
  const [activeUserConfig, setActiveUserConfig] = useState<string | null>(null);
  const [showRyftConfigModal, setShowRyftConfigModal] = useState(false);
  const [ryftAccountStatus, setRyftAccountStatus] = useState<string>("");
  const [isAccountRegistered, setIsAccountRegistered] = useState(false);
  const [installBtnLoader, setInstallBtnLoader] = useState(false);

  // Initialize API keys for already installed locations
  React.useEffect(() => {
    if (installedAppData?.apiKeys) {
      setLocationApiKeys(installedAppData.apiKeys);
    }
  }, [installedAppData]);

  useEffect(() => {
    if (app.marketType === 1 && installedAppData?.apiKeys) {
      setUserApiKeys(installedAppData.apiKeys);
    }
  }, [installedAppData, app.marketType]);

  // Check account registration status for Ryft
  useEffect(() => {
    const checkAccountRegistration = async () => {
      if (app.marketType === 0 && installedAppData) {
        const firstLocationId = installedAppData.locations[0];
        const location = locations.find((l) => l.id === firstLocationId);

        if (location && companyId) {
          try {
            const companyMarketApps = await getCompanyMarketplaceApps(
              companyId,
            );
            const ryftApp = companyMarketApps.find(
              (marketApp: any) =>
                marketApp.marketAppID === app.id &&
                marketApp.ods === location.ods,
            );

            if (ryftApp) {
              setIsAccountRegistered(ryftApp.isAccountRegistered || false);
            }
          } catch (error) {
            console.error("Error checking account registration:", error);
          }
        }
      }
    };
    checkAccountRegistration();
  }, [app.id, app.marketType, installedAppData, locations, companyId]);

  useEffect(() => {
    if (app.marketType === 4 && installedAppData?.apiKeys) {
      const companyConfig = installedAppData.apiKeys["company"];
      if (companyConfig?.secretKey) {
        setSecretKey(companyConfig.secretKey);
      }
    }
  }, [installedAppData, app.marketType]);

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId],
    );
  };

  const handleCopySecretKey = async () => {
    if (!secretKey) return;

    await navigator.clipboard.writeText(secretKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleRyftSubmit = () => {
    if (!currentPharmacyId) return;
    handleInstallSubmit([currentPharmacyId]);
  };

  const handleInstallSubmit = async (locationIdsOverride?: string[]) => {
    setInstallBtnLoader(true);
    const targetLocations = Array.isArray(locationIdsOverride)
      ? locationIdsOverride
      : selectedLocations;
    trackFirstTimeIntercomEvent({
      eventName: "marketplace_opened_first_time",
      user: {
        id: currentUser.emailAddress,
        email: currentUser.emailAddress,
        name: currentUser.fullName,
      },
      properties: {
        source: "marketplace_settings",
      },
    });
    if (app.marketType === 1) {
      if (selectedUsers.length === 0) return;

      for (const userId of selectedUsers) {
        const user = users.find((u) => u.id === userId);
        if (!user) continue;

        const companyMarketApp = {
          companyID: companyId,
          ods: null,
          marketAppID: app.id,
          marketAppName: app.name,
          marketType: app.marketType,
          payLoad: null, //JSON.stringify({ licenseKey: "", email: user.email }),
          email: user.email,
          isAccountRegistered: true,
          isActive: true,
        };

        await createCompanyMarketApp(companyMarketApp);
      }

      await refreshInstalledApps();
      setShowInstallModal(false);
      setSelectedUsers([]);
      return;
    }

    if (app.marketType === 4) {
      try {
        const companyMarketApp = {
          companyID: companyId,
          ods: null,
          marketAppID: app.id,
          marketAppName: app.name,
          marketType: app.marketType,
          payLoad: JSON.stringify({ secretKey: "" }),
          email: null,
          isAccountRegistered: true,
          isActive: true,
        };

        await createCompanyMarketApp(companyMarketApp);

        await refreshInstalledApps();
        setShowInstallModal(false);
      } catch (error) {
        console.error("Error installing Pharmappy:", error);
      }
      return;
    }

    if (targetLocations.length === 0) return;

    const apiKeys: Record<string, any> = {};
    targetLocations.forEach((locationId) => {
      apiKeys[locationId] = {
        apiKey: "",
        userId: "",
        webhookUrl: "",
        testMode: true,
      };
    });

    if (app.marketType === 0) {
      try {
        const companies = await getCompanies(1, companyId);

        if (companies && companies.length > 0) {
          const company = companies[0];

          const companyMarketApps = await getCompanyMarketplaceAppsByCompanyId(
            companyId,
          );
          const ryftApp = companyMarketApps.find(
            (marketApp: any) =>
              marketApp.marketAppID === app.id && marketApp.marketType === 0,
          );

          const emailToValidate =
            ryftApp?.email || company.companyOwnerEmailAddress;

          for (const locationId of targetLocations) {
            await handleCompanyMarketAppSave(locationId, emailToValidate);
          }

          const validationResult = await validateAccountRegistered(
            emailToValidate,
          );

          if (validationResult) {
            const statusCode = parseInt(validationResult.ryftStatusCode);

            if (statusCode === 404) {
              const accountData = {
                email: company.companyOwnerEmailAddress,
                companyName: company.companyName,
                companyID: company.companyID,
              };

              const accountResult = await createAccount(accountData);

              if (!accountResult) {
                console.error("Failed to create Ryft account");
              } else {
                showSuccess("Ryft signup email is sent.");
              }
            } else {
              showSuccess(validationResult.ryftMessage);
              setRyftAccountStatus(validationResult.ryftMessage);
            }
          }
        }
      } catch (error) {
        console.error("Error creating Ryft account:", error);
        setRyftAccountStatus("Error validating/creating Ryft account");
        return;
      }
    } else {
      for (const locationId of targetLocations) {
        await handleCompanyMarketAppSave(locationId);
      }
    }

    onInstall(app.id, targetLocations, apiKeys);
    await refreshInstalledApps();
    setShowInstallModal(false);
    setSelectedLocations([]);
    setInstallBtnLoader(false);
  };

  const handleApiKeysSave = async (
    locationOrUserId: string,
    apiKeys: Record<string, any>,
  ) => {
    if (app.marketType === 1) {
      const user = users.find((u) => u.id === locationOrUserId);
      if (!user) return;

      const payLoadData = {
        licenseKey: apiKeys?.apiKey,
        email: user.email,
      };

      const companyMarketApp = {
        companyID: companyId,
        ods: "",
        marketAppID: app.id,
        marketAppName: app.name,
        marketType: app.marketType,
        payLoad: JSON.stringify(payLoadData),
        email: user.email,
        isAccountRegistered: true,
        isActive: true,
      };

      await createCompanyMarketApp(companyMarketApp);
      onApiKeysUpdate(app.id, locationOrUserId, apiKeys);
      await refreshInstalledApps();
      setActiveUserConfig(null);
      return;
    }

    const location = locations.find((l) => l.id === locationOrUserId);
    let payLoadData;

    if (app.marketType === 4) {
      payLoadData = {
        secretKey: apiKeys?.secretKey || "",
      };
    } else {
      payLoadData = apiKeys;
    }

    const companyMarketApp = {
      companyID: companyId,
      ods: location?.ods,
      marketAppID: app.id,
      marketAppName: app.name,
      marketType: app.marketType,
      payLoad: JSON.stringify(payLoadData),
      email: apiKeys?.email,
      isAccountRegistered: true,
      isActive: true,
    };

    await createCompanyMarketApp(companyMarketApp);

    onApiKeysUpdate(app.id, locationOrUserId, apiKeys);
    await refreshInstalledApps();
    setActiveLocationConfig(null);
  };

  const handleCompanyMarketAppSave = async (
    locationId: string,
    email?: string,
  ) => {
    const location = locations.find((l) => l.id === locationId);

    const companyMarketApp = {
      companyID: companyId,
      ods: location.ods,
      marketAppID: app.id,
      marketAppName: app.name,
      marketType: app.marketType,
      payLoad: null,
      email: app.marketType === 0 ? email : null,
      isAccountRegistered: app.marketType === 0 ? false : true,
      isActive: true,
    };

    await createCompanyMarketApp(companyMarketApp);
  };

  const handleUninstall = async (appId: string, locationOrUserId?: string) => {
    if (app.marketType === 1) {
      // User-based uninstall
      if (!locationOrUserId) {
        // Uninstall from all users
        for (const userId of Object.keys(installedAppData?.apiKeys || {})) {
          const user = users.find((u) => u.id === userId);
          if (!user) continue;

          const deleteCompanyMarketAppData = {
            companyID: companyId,
            email: user.email,
            marketType: app.marketType,
            isActive: false,
          };
          await deleteCompanyMarketApp(deleteCompanyMarketAppData);
        }
      } else {
        // Uninstall from specific user
        const user = users.find((u) => u.id === locationOrUserId);
        if (!user) return;

        const deleteCompanyMarketAppData = {
          companyID: companyId,
          email: user.email,
          marketType: app.marketType,
          isActive: false,
        };
        await deleteCompanyMarketApp(deleteCompanyMarketAppData);
      }

      onUninstall(appId, locationOrUserId);
      await refreshInstalledApps();
      return;
    }

    if (app.marketType === 4) {
      const deleteCompanyMarketAppData = {
        companyID: companyId,
        ods: null,
        marketType: app.marketType,
        isActive: false,
      };
      await deleteCompanyMarketApp(deleteCompanyMarketAppData);
      onUninstall(appId);
      await refreshInstalledApps();
      return;
    }

    if (!locationOrUserId) {
      for (const locId of installedAppData?.locations || []) {
        const location = locations.find((l) => l.id === locId);
        if (!location) continue;

        const deleteCompanyMarketAppData = {
          companyID: companyId,
          ods: location.ods,
          marketType: app.marketType,
          isActive: false,
        };
        await deleteCompanyMarketApp(deleteCompanyMarketAppData);
      }
    } else {
      // Uninstall from specific location
      const location = locations.find((l) => l.id === locationOrUserId);
      if (!location) return;

      const deleteCompanyMarketAppData = {
        companyID: companyId,
        ods: location.ods,
        marketType: app.marketType,
        isActive: false,
      };

      await deleteCompanyMarketApp(deleteCompanyMarketAppData);
    }

    onUninstall(appId, locationOrUserId);
    await refreshInstalledApps();
  };

  const handleRyftRecreateLink = async () => {
    try {
      const companyMarketApps = await getCompanyMarketplaceAppsByCompanyId(
        companyId,
      );
      const ryftApp = companyMarketApps.find(
        (marketApp: any) =>
          marketApp.marketAppID === app.id && marketApp.marketType === 0,
      );

      if (!ryftApp?.email) {
        setRyftAccountStatus("No email found in CompanyMarketApp");
        return;
      }

      const emailToUse = ryftApp.email;

      const validationResult = await validateAccountRegistered(emailToUse);

      if (validationResult) {
        const statusCode = parseInt(validationResult.ryftStatusCode);

        if (statusCode !== 200) {
          const companies = await getCompanies(1, companyId);

          if (companies && companies.length > 0) {
            const company = companies[0];

            const linkData = {
              email: emailToUse,
              companyName: company.companyName,
              companyID: company.companyID,
            };

            const linkResult = await recreateLink(linkData);

            if (linkResult) {
              setRyftAccountStatus("Link recreated successfully");
              showSuccess("Link recreated successfully");
            } else {
              setRyftAccountStatus("Failed to recreate link");
            }
          }
        } else {
          setRyftAccountStatus(validationResult.ryftMessage);
        }
      } else {
        setRyftAccountStatus("Failed to validate account");
      }
    } catch (error) {
      console.error("Error recreating link:", error);
      setRyftAccountStatus("Error recreating link");
    }
  };

  const handleRyftValidateAccount = async () => {
    try {
      const companyMarketApps = await getCompanyMarketplaceAppsByCompanyId(
        companyId,
      );
      const ryftApp = companyMarketApps.find(
        (marketApp: any) =>
          marketApp.marketAppID === app.id && marketApp.marketType === 0,
      );

      if (!ryftApp?.email) {
        setRyftAccountStatus("No email found in CompanyMarketApp");
        return;
      }

      const validationResult = await validateAccountRegistered(ryftApp.email);

      if (validationResult.ryftMessage) {
        setRyftAccountStatus(validationResult.ryftMessage);
      } else {
      setRyftAccountStatus("Validation failed");
      }
    } catch (error) {
      console.error("Error validating account:", error);
      setRyftAccountStatus("Error validating account");
    }
  };

  if (IsMarketplaceAppLoading) {
    return <AppDetailsSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* App Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="text-5xl">
            {app.imageUrl ? (
              <img
                src={app.imageUrl}
                alt={app.name}
                className="object-contain w-16 h-16"
              />
            ) : (
              app.imageEmoji
            )}
          </div>
          <div>
            <h2
              className={`text-2xl font-bold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {app.name}
            </h2>
            <p
              className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              by {app.developer}
            </p>
            <Badge variant="secondary" className="mt-2">
              {app.category}
            </Badge>
          </div>
        </div>
      </div>

      {/* Description & Details */}

      <div className="grid gap-6 md:grid-cols-3 cc">
        <div className="space-y-6 lg:col-span-2">
          {isMarketAppsLoading ? (
            <>
              {/* About this app - Skeleton */}
              <div>
                <Skeleton
                  variant="text"
                  width={150}
                  height={30}
                  sx={{
                    bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                  }}
                />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={60}
                  sx={{
                    bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                  }}
                />

                <div className="grid grid-cols-2 gap-4 mt-4 mb-4">
                  <div>
                    <Skeleton
                      variant="text"
                      width={80}
                      height={20}
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : undefined,
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={120}
                      height={20}
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : undefined,
                      }}
                    />
                  </div>
                  <div>
                    <Skeleton
                      variant="text"
                      width={80}
                      height={20}
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : undefined,
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={120}
                      height={20}
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : undefined,
                      }}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <Skeleton
                    variant="text"
                    width={120}
                    height={20}
                    sx={{
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="100%"
                    height={40}
                    sx={{
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                    }}
                  />
                </div>
              </div>

              {/* Features - Skeleton */}
              <div>
                <Skeleton
                  variant="text"
                  width={100}
                  height={30}
                  sx={{
                    bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                  }}
                />
                <div className="grid grid-cols-2 gap-2">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <Skeleton
                        variant="circular"
                        width={16}
                        height={16}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255,255,255,0.1)"
                            : undefined,
                        }}
                      />
                      <Skeleton
                        variant="text"
                        width={150}
                        height={20}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255,255,255,0.1)"
                            : undefined,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <h3
                  className={`font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  About
                </h3>
                <p
                  className={`mb-4 transition-colors duration-300 ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {app.description}
                </p>

                {/* <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Category
                    </Label>
                    <p
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {app.category}
                    </p>
                  </div>
                  <div>
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Price
                    </Label>
                    <p
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {app.price}
                    </p>
                  </div>
                </div> */}

                {/* <div className="mb-4">
                  <Label
                    className={`text-sm font-medium mb-2 block transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Requirements
                  </Label>
                  <p
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {app.requirements}
                  </p>
                </div> */}
              </div>

              <div>
                <h3
                  className={`font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Features
                </h3>
                <ul className="space-y-2">
                  {app.features.map((feature, index) => (
                    <li
                      key={index}
                      className={`flex items-center gap-2 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {/* Location-specific configurations - only show if installed */}
          {isInstalled && app.marketType !== 0 && (
            <div>
              <h3
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {app.marketType === 4
                  ? "Configuration"
                  : app.marketType === 1
                  ? "Member Licenses"
                  : "Store Configurations"}
              </h3>
              <div className="space-y-4">
                {app.marketType === 4 ? (
                  <div
                    className={`border rounded-lg p-4 transition-colors duration-300 ${
                      isDarkMode
                        ? "border-gray-700 bg-gray-800"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Status: Configured
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveLocationConfig("company")}
                        >
                          <Key className="w-3 h-3 mr-1" />
                          Configure
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUninstall(app.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : app.marketType === 1 ? (
                  // DemDx User-based configurations
                  Object.keys(installedAppData!.apiKeys || {}).map((userId) => {
                    const user = users.find((u) => u.id === userId);
                    const apiKeys = userApiKeys[userId] || {};
                    if (!user) return null;

                    return (
                      <div
                        key={userId}
                        className={`border rounded-lg p-4 transition-colors duration-300 ${
                          isDarkMode
                            ? "border-gray-700 bg-gray-800"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4
                              className={`font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {user.title} {user.forename} {user.surname}
                            </h4>
                            <p
                              className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {user.email} • {user.role}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setActiveUserConfig(userId)}
                            >
                              <Key className="w-3 h-3 mr-1" />
                              Configure
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUninstall(app.id, userId)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Status:{" "}
                          {apiKeys.apiKey
                            ? "License configured"
                            : "Needs license key"}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  installedAppData!.locations.map((locationId) => {
                    const location = locations.find((l) => l.id === locationId);
                    const apiKeys = locationApiKeys[locationId] || {};
                    if (!location) {
                      return null;
                    }

                    return (
                      <div
                        key={locationId}
                        className={`border rounded-lg p-4 transition-colors duration-300 ${
                          isDarkMode
                            ? "border-gray-700 bg-gray-800"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4
                              className={`font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {location.name}
                            </h4>
                            <p
                              className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {location.address}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleUninstall(app.id, locationId)
                              }
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Status:{" "}
                          {app.marketType === 4
                            ? apiKeys.secretKey
                              ? "Configured"
                              : "Needs configuration"
                            : app.marketType === 0
                            ? apiKeys.isAccountRegistered
                              ? "Configured"
                              : "Needs configuration"
                            : apiKeys.apiKey
                            ? "Configured"
                            : "Needs configuration"}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/*Sidebar */}
        <div className="space-y-4">
          {isMarketAppsLoading ? (
            <>
              {/* Pricing Card - Skeleton */}
              <div
                className={`border rounded-lg p-4 transition-colors duration-300 ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className="space-y-4 text-center">
                  <Skeleton
                    variant="text"
                    width={100}
                    height={40}
                    sx={{
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                      margin: "0 auto",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={40}
                    sx={{
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                      borderRadius: "8px",
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={20}
                    sx={{
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : undefined,
                      margin: "0 auto",
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className={`border rounded-lg p-4 transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <h4
                  className={`text-sm font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Details
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span
                      className={`transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Price:
                    </span>
                    <span
                      className={`ml-2 transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      £{app.price}
                    </span>
                  </div>
                  <div>
                    <span
                      className={`transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Requirements:
                    </span>
                    <span
                      className={`ml-2 transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {app.requirements}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {isInstalled ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {app.marketType === 4 || app.marketType === 0
                          ? "Installed"
                          : app.marketType === 1
                          ? `${
                              Object.keys(installedAppData!.apiKeys || {})
                                .length
                            } license${
                              Object.keys(installedAppData!.apiKeys || {})
                                .length !== 1
                                ? "s"
                                : ""
                            } assigned`
                          : `Installed on ${
                              installedAppData!.locations.length
                            } location${
                              installedAppData!.locations.length !== 1
                                ? "s"
                                : ""
                            }`}
                      </span>
                    </div>
                    {app.marketType !== 0 && app.marketType !== 4 && (
                      <Button
                        className="w-full bg-slate-900 hover:bg-slate-800"
                        onClick={() => setShowInstallModal(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {app.marketType === 1
                          ? "Add to More Users"
                          : "Add to More Locations"}
                      </Button>
                    )}
                    {app.marketType !== 1 && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() =>
                          app.marketType === 0
                            ? setShowRyftConfigModal(true)
                            : setActiveLocationConfig(
                                installedAppData!.locations[0],
                              )
                        }
                      >
                        <Key className="w-3 h-3 mr-1" />
                        Configure
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleUninstall(app.id)}
                    >
                      Uninstall Completely
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    onClick={() => {
                      if (app.marketType === 4) handleInstallSubmit();
                      else if (app.marketType === 0) handleRyftSubmit();
                      else setShowInstallModal(true);
                    }}
                    disabled={installBtnLoader}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {installBtnLoader ? (
                      <>
                        Install App
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      </>
                    ) : (
                      "Install App"
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showInstallModal && app.marketType !== 4 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className={`max-w-md w-full mx-4 rounded-lg p-6 transition-colors duration-300 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {app.marketType === 1 ? "Select Users" : "Select Store Locations"}
            </h3>
            <p
              className={`text-sm mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {app.marketType === 1
                ? `Assign ${app.name} licenses to users. Each user will need their own license key.`
                : `Choose which store locations you want to install ${app.name} on. Each location will require its own API configuration.`}
            </p>

            <div className="space-y-3 overflow-y-auto max-h-64">
              {app.marketType === 1
                ? // User selection for DemDx
                  users
                    .filter((user) => user.active)
                    .filter((user) => !installedAppData?.apiKeys?.[user.id])
                    .map((user) => (
                      <div
                        key={user.id}
                        className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors duration-300 ${
                          selectedUsers.includes(user.id)
                            ? isDarkMode
                              ? "border-blue-500 bg-blue-900/20"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                            ? "border-gray-600 hover:bg-gray-700"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                        onClick={() => handleUserToggle(user.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <div
                            className={`font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {user.title} {user.forename} {user.surname}
                          </div>
                          <div
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {user.email} • {user.role}
                          </div>
                        </div>
                      </div>
                    ))
                : // Location selection for other apps
                  locations
                    .filter((location) => location.active)
                    .filter(
                      (location) =>
                        !installedAppData?.locations?.includes(location.id),
                    )
                    .map((location) => (
                      <div
                        key={location.id}
                        className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors duration-300 ${
                          selectedLocations.includes(location.id)
                            ? isDarkMode
                              ? "border-blue-500 bg-blue-900/20"
                              : "border-blue-500 bg-blue-50"
                            : isDarkMode
                            ? "border-gray-600 hover:bg-gray-700"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                        onClick={() => handleLocationToggle(location.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLocations.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <div
                            className={`font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {location.name}
                          </div>
                          <div
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {location.address}
                          </div>
                        </div>
                      </div>
                    ))}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowInstallModal(false);
                  setSelectedLocations([]);
                  setSelectedUsers([]);
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-slate-900 hover:bg-slate-800"
                onClick={handleInstallSubmit}
                disabled={
                  app.marketType === 1
                    ? selectedUsers.length === 0
                    : selectedLocations.length === 0
                }
              >
                {app.marketType === 1 ? (
                  <>
                    Assign {selectedUsers.length} license
                    {selectedUsers.length !== 1 ? "s" : ""}
                  </>
                ) : (
                  <>
                    Install on {selectedLocations.length} location
                    {selectedLocations.length !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ryft Configuration Modal */}
      {showRyftConfigModal && app.marketType === 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className={`max-w-md w-full mx-4 rounded-lg p-6 transition-colors duration-300 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Ryft Account Management
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowRyftConfigModal(false);
                  setRyftAccountStatus("");
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Account Status Badge */}
            {isAccountRegistered && (
              <div className="flex items-center gap-2 p-3 mb-4 text-green-800 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Account is registered
                </span>
              </div>
            )}

            {ryftAccountStatus && (
              <div
                className={`p-3 rounded-lg mb-4 ${
                  ryftAccountStatus.includes("success")
                    ? "bg-green-100 text-green-800"
                    : ryftAccountStatus.includes("Error") ||
                      ryftAccountStatus.includes("Failed")
                    ? "bg-red-100 text-red-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {ryftAccountStatus}
              </div>
            )}

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={handleRyftRecreateLink}
                disabled={isAccountRegistered}
              >
                {"Recreate Link"}
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={handleRyftValidateAccount}
                disabled={isAccountRegistered}
              >
                {"Validate Account"}
              </Button>
            </div>

            {isAccountRegistered && (
              <p
                className={`text-xs text-center mt-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                These actions are disabled because the account is already
                registered
              </p>
            )}
          </div>
        </div>
      )}

      {activeLocationConfig === "company" && app.marketType === 4 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className={`max-w-md w-full mx-4 rounded-lg p-6 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {app.name} Configuration
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveLocationConfig(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-2">
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Secret Key
              </p>

              <div
                className={`flex items-center justify-between rounded-md px-3 py-2 ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <span className="font-mono text-sm break-all">
                  {showSecretKey
                    ? secretKey
                    : "•".repeat(Math.min(secretKey?.length || 0, 20))}
                </span>

                <div className="flex items-center gap-3">
                  {/* Copy */}
                  <button
                    type="button"
                    onClick={handleCopySecretKey}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>

                  {/* Toggle */}
                  <button
                    type="button"
                    onClick={() => setShowSecretKey((prev) => !prev)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
                  >
                    {showSecretKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setActiveLocationConfig(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DemDx User Configuration Modal */}
      {activeUserConfig && app.marketType === 1 && (
        <LocationApiConfigModal
          user={users.find((u) => u.id === activeUserConfig)}
          app={app}
          currentApiKeys={userApiKeys[activeUserConfig] || {}}
          onSave={(apiKeys) => handleApiKeysSave(activeUserConfig, apiKeys)}
          onClose={() => setActiveUserConfig(null)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Location Configuration Modal (Other Apps) */}
      {activeLocationConfig && app.marketType !== 1 && app.marketType !== 4 && (
        <LocationApiConfigModal
          location={locations.find((l) => l.id === activeLocationConfig)!}
          app={app}
          currentApiKeys={locationApiKeys[activeLocationConfig] || {}}
          onSave={(apiKeys) => handleApiKeysSave(activeLocationConfig, apiKeys)}
          onClose={() => setActiveLocationConfig(null)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}
